import type { InputHTMLAttributes, ReactNode } from 'react';

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  helperText?: ReactNode;
  errorText?: ReactNode;
}

/** Input etiquetado, fuente >=16px para evitar el zoom de Safari y alto cómodo de toque. */
export function Field({
  label,
  id,
  helperText,
  errorText,
  autoFocus,
  'aria-describedby': externalDescription,
  'aria-invalid': externalInvalid,
  ...props
}: FieldProps) {
  const helperId = helperText ? `${id}-helper` : undefined;
  const errorId = errorText ? `${id}-error` : undefined;
  const describedBy =
    [externalDescription, helperId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="block">
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-slate-700">
        {label}
      </label>
      <input
        id={id}
        autoFocus={autoFocus}
        data-modal-autofocus={autoFocus ? 'true' : undefined}
        aria-describedby={describedBy}
        aria-invalid={externalInvalid ?? (errorText ? true : undefined)}
        className="tap-target w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base text-slate-900 outline-none transition placeholder:text-slate-500 focus:border-slate-400 focus:ring-4 focus:ring-slate-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-600 read-only:cursor-not-allowed read-only:bg-slate-50 read-only:text-slate-600"
        {...props}
      />
      {helperText && (
        <span id={helperId} className="mt-1.5 block text-xs leading-4 text-slate-500">
          {helperText}
        </span>
      )}
      {errorText && (
        <span id={errorId} role="alert" className="mt-1.5 block text-xs leading-4 text-expense">
          {errorText}
        </span>
      )}
    </div>
  );
}
